namespace SolovisGateway.Admin.Organizations {

    export const ValidateConnectionOutcome = {
        TestLinkGenerated: "TestLinkGenerated",
        NoAuth0Connection: "NoAuth0Connection",
        BackendError: "BackendError",
    } as const;
    export type ValidateConnectionOutcome = typeof ValidateConnectionOutcome[keyof typeof ValidateConnectionOutcome];

    export interface OrgValidateConnectionDialogOptions extends SupportContactOptions {
        actionLabel: string;
        outcome: ValidateConnectionOutcome;
        testLink?: string;
        /** From GlobalConfig; omit / null / ≤0 hides the "within N hours" sentence. */
        enableTestSignInFreshnessHours?: number | null;
        onTestSucceeded?: () => void;
        onClose?: () => void;
    }

    export class OrgValidateConnectionDialog {

        private _options: OrgValidateConnectionDialogOptions;

        constructor(options: OrgValidateConnectionDialogOptions) {
            this._options = options;
        }

        public show() {
            var self = this;
            var body = $("<div>").css({ "padding": "0 10px" });

            switch (this._options.outcome) {
                case ValidateConnectionOutcome.TestLinkGenerated:
                    body.append(this._renderTestLinkGenerated());
                    break;
                case ValidateConnectionOutcome.NoAuth0Connection:
                    body.append(this._renderNoAuth0Connection());
                    break;
                case ValidateConnectionOutcome.BackendError:
                    body.append(this._renderBackendError());
                    break;
                default:
                    body.append(this._renderBackendError());
                    break;
            }

            var dialog = null;
            dialog = new (Solovis as any).UI.Dialog({
                container: body,
                title: "Validate this connection",
                modal: true,
                autoOpen: true,
                center: true,
                destroyOnClose: true,
                actions: ["Close"],
                width: 600,
                close: function () {
                    if (self._options.onClose) {
                        self._options.onClose();
                    }
                },
                buttons: [
                    {
                        label: "Close",
                        click: function () {
                            dialog.close();
                        }
                    }
                ]
            });
        }

        private _renderTestLinkGenerated(): JQuery {
            var self = this;
            var content = $("<div>");

            content.append($("<p>").text(
                "Share this link with the organization's IT support team. They sign in with it."
            ));

            var linkBox = $("<div>").css({
                "border": "1px solid #ddd",
                "padding": "15px",
                "margin-bottom": "15px"
            });
            linkBox.append($("<div>").css({
                "font-weight": "bold",
                "font-size": "11px",
                "margin-bottom": "8px",
                "color": "#666",
                "text-transform": "uppercase",
                "letter-spacing": "0.5px"
            }).text("Test Link"));

            var linkRow = $("<div>").css({
                "display": "flex",
                "align-items": "center",
                "gap": "10px"
            });
            var linkText = $("<input>")
                .attr("type", "text")
                .attr("aria-label", "Test link")
                .prop("readonly", true)
                .val(this._options.testLink || "")
                .css({
                    "border": "1px solid #ddd",
                    "padding": "8px 10px",
                    "font-family": "monospace",
                    "font-size": "13px",
                    "flex": "1",
                    "min-width": "0",
                    "background-color": "#f9f9f9"
                });
            linkRow.append(linkText);

            var copyButton = new Solovis.UI.Buttons.Button({
                text: "Copy Link",
                size: "small",
                click: function () {
                    self._copyToClipboard(self._options.testLink || "");
                }
            });
            linkRow.append(copyButton.getElement());
            linkBox.append(linkRow);
            content.append(linkBox);

            var warningBanner = $("<div>").attr("role", "note").css({
                "background-color": "#fcf8e3",
                "border": "1px solid #faebcc",
                "color": "#8a6d3b",
                "padding": "12px 15px",
                "border-radius": "4px",
                "margin-bottom": "15px"
            });
            warningBanner.append($("<p>").css({ "margin": "0 0 10px 0" }).text(
                "Sign-in has to start from this link. Starting sign-in from the identity provider side, such as an app tile in the IdP portal, is not supported and will fail."
            ));
            warningBanner.append($("<p>").css({ "margin": "0" }).text(
                "The test has to be run with a user who is already added into the application. The link triggers a sign-in for such a user."
            ));
            content.append(warningBanner);

            content.append($("<p>").css({ "font-weight": "bold" }).text("What to check"));
            var whatToCheckList = $("<ul>").css({ "margin-bottom": "15px" });
            whatToCheckList.append($("<li>").text(
                "On a successful sign-in, the browser is redirected to jwt.io, which shows the decoded token."
            ));
            whatToCheckList.append($("<li>").text(
                "In the decoded payload, confirm the email claim is present and shows the exact email address expected for that user."
            ));
            content.append(whatToCheckList);

            var enableNoteText = "After the test, the connection can be enabled.";
            var freshnessHours = this._options.enableTestSignInFreshnessHours;
            if (typeof freshnessHours === "number" && freshnessHours > 0) {
                enableNoteText +=
                    " Enable has to be done within " +
                    freshnessHours +
                    " hours of a successful test.";
            }
            content.append($("<div>").css({
                "border": "1px solid #ddd",
                "padding": "12px 15px",
                "background-color": "#f9f9f9"
            }).text(enableNoteText));

            return content;
        }

        private _renderNoAuth0Connection(): JQuery {
            var content = $("<div>");
            var actionInstruction = this._options.actionLabel === "Configure"
                ? "create the connection first."
                : "complete setup first.";

            var warningBanner = $("<div>").attr("role", "alert").css({
                "background-color": "#fcf8e3",
                "border": "1px solid #faebcc",
                "color": "#8a6d3b",
                "padding": "12px 15px",
                "border-radius": "4px",
                "margin-bottom": "15px"
            }).text(
                "No Auth0 connection found with this name. Click " +
                this._options.actionLabel +
                " to " +
                actionInstruction
            );
            content.append(warningBanner);

            return content;
        }

        private _renderBackendError(): JQuery {
            var content = $("<div>");
            var errorBanner = $("<div>").attr("role", "alert").css({
                "background-color": "#f2dede",
                "border": "1px solid #ebccd1",
                "color": "#a94442",
                "padding": "12px 15px",
                "border-radius": "4px"
            });
            errorBanner.append($("<span>").text(
                "We could not generate the test link. Please try again in a little while. "
            ));
            SsoConnectionStatusUi.appendSupportContactLine(errorBanner, this._options, "If the problem continues, ", ".");
            content.append(errorBanner);

            return content;
        }

        private _copyToClipboard(text: string) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text);
            }
            else {
                var textArea = $("<textarea>")
                    .val(text)
                    .css({ "position": "fixed", "opacity": "0" })
                    .appendTo(document.body);
                (textArea[0] as HTMLTextAreaElement).select();
                document.execCommand("copy");
                textArea.remove();
            }
        }
    }
}
